import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip,
  LinearProgress,
  Avatar
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { task } from '../../../utils/api';
import { TASK_STATUS, TASK_PRIORITY } from '../../../constants/task';
import TaskStatCard from './TaskStatCard';
import WorkshopCarsSection from './WorkshopCarsSection';
import { 
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import CreateTaskModal from './CreateTaskModal';
import { useAuth } from '../../../components/auth/AuthModule';

const WorkshopDashboard = () => {
  const { t } = useTranslation('workshop');
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showOnlyMine, setShowOnlyMine] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [showOnlyMine]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      if (showOnlyMine) {
        const data = await task.getStats({ userId: user._id });
        setUserStats(data);
        setStats(null);
      } else {
        const [allData, userData] = await Promise.all([
          task.getStats(),
          task.getStats({ userId: user._id })
        ]);
        setStats(allData);
        setUserStats(userData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const getStatsByStatus = (status, source = 'all') => {
    const data = source === 'user' ? userStats : stats;
    return data?.find(stat => stat._id === status) || { 
      count: 0, 
      estimatedHours: 0, 
      actualHours: 0,
      completedTasks: 0,
      inProgressTasks: 0,
      pendingTasks: 0
    };
  };

  const calculateTotalHours = (source = 'all') => {
    const data = source === 'user' ? userStats : stats;
    return data?.reduce((acc, stat) => ({
      estimated: acc.estimated + (stat.estimatedHours || 0),
      actual: acc.actual + (stat.actualHours || 0)
    }), { estimated: 0, actual: 0 }) || { estimated: 0, actual: 0 };
  };

  const currentStats = showOnlyMine ? userStats : stats;
  const totalTasks = currentStats?.reduce((acc, stat) => acc + stat.count, 0) || 0;
  const totalHours = calculateTotalHours(showOnlyMine ? 'user' : 'all');

  const getEfficiencyRating = () => {
    const { estimated, actual } = calculateTotalHours(showOnlyMine ? 'user' : 'all');
    if (estimated === 0 || actual === 0) return 100;
    return Math.round((estimated / actual) * 100);
  };

  const getProgressColor = (value) => {
    if (value >= 90) return 'success.main';
    if (value >= 70) return 'primary.main';
    return 'warning.main';
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <Button
            variant="contained"
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            {t('dashboard.createTask')}
          </Button>
        </Box>
      </Box>

      {/* Cars Section */}
      <Box mb={4}>
        <WorkshopCarsSection />
      </Box>

      <Grid container spacing={3}>
        {/* Achievement Cards
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(120deg, #2196f3 0%, #1976d2 100%)',
            color: 'white',
            height: '100%'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <CheckCircleIcon />
                </Avatar>
                <Typography variant="h6">
                  {t('dashboard.completionRate')}
                </Typography>
              </Box>
              <Typography variant="h3" gutterBottom>
                {Math.round((getStatsByStatus(TASK_STATUS.COMPLETED, showOnlyMine ? 'user' : 'all').count / totalTasks) * 100) || 0}%
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {t('dashboard.tasksCompleted', { count: getStatsByStatus(TASK_STATUS.COMPLETED, showOnlyMine ? 'user' : 'all').count })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(120deg, #4caf50 0%, #388e3c 100%)',
            color: 'white',
            height: '100%'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <SpeedIcon />
                </Avatar>
                <Typography variant="h6">
                  {t('dashboard.efficiency')}
                </Typography>
              </Box>
              <Typography variant="h3" gutterBottom>
                {getEfficiencyRating()}%
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {t('dashboard.efficiencyDesc')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(120deg, #ff9800 0%, #f57c00 100%)',
            color: 'white',
            height: '100%'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <StarIcon />
                </Avatar>
                <Typography variant="h6">
                  {t('dashboard.contribution')}
                </Typography>
              </Box>
              <Typography variant="h3" gutterBottom>
                {totalHours.actual.toFixed(1)}h
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {t('dashboard.contributionDesc')}
              </Typography>
            </CardContent>
          </Card>
        </Grid> */}

        {/* Progress Overview
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <TrendingUpIcon color="primary" />
                <Typography variant="h6">
                  {t('dashboard.progressOverview')}
                </Typography>
              </Box>
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box mb={2}>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('dashboard.plannedWork')}
                    </Typography>
                    <Box display="flex" alignItems="baseline" gap={1}>
                      <Typography variant="h4" color="primary">
                        {totalHours.estimated.toFixed(1)}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {t('common.hours')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box mb={2}>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('dashboard.accomplished')}
                    </Typography>
                    <Box display="flex" alignItems="baseline" gap={1}>
                      <Typography variant="h4" sx={{ color: getProgressColor(getEfficiencyRating()) }}>
                        {totalHours.actual.toFixed(1)}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {t('common.hours')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('dashboard.progressTracker')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={totalHours.estimated > 0 ? (totalHours.actual / totalHours.estimated) * 100 : 0}
                          sx={{ 
                            height: 10, 
                            borderRadius: 5,
                            backgroundColor: 'rgba(0,0,0,0.05)',
                            '& .MuiLinearProgress-bar': {
                              background: `linear-gradient(90deg, ${getProgressColor(getEfficiencyRating())} 0%, ${getProgressColor(getEfficiencyRating())} 100%)`
                            }
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: getProgressColor(getEfficiencyRating()) }}>
                        {totalHours.estimated > 0 
                          ? `${Math.round((totalHours.actual / totalHours.estimated) * 100)}%`
                          : '0%'
                        }
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: getProgressColor(getEfficiencyRating()) }}>
                      {t('dashboard.progressMessage', {
                        hours: totalHours.actual.toFixed(1),
                        total: totalHours.estimated.toFixed(1)
                      })}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>  */}

        {/* Task Status Summary */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Typography variant="h6">
                  {t('dashboard.taskSummary')}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TaskStatCard
                    title={t('status.inProgress')}
                    count={getStatsByStatus(TASK_STATUS.IN_PROGRESS, showOnlyMine ? 'user' : 'all').count}
                    color="info"
                    icon={<TrendingUpIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TaskStatCard
                    title={t('status.completed')}
                    count={getStatsByStatus(TASK_STATUS.COMPLETED, showOnlyMine ? 'user' : 'all').count}
                    color="success"
                    icon={<CheckCircleIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TaskStatCard
                    title={t('status.pending')}
                    count={getStatsByStatus(TASK_STATUS.PENDING, showOnlyMine ? 'user' : 'all').count}
                    color="warning"
                    icon={<CheckCircleIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TaskStatCard
                    title={t('status.total')}
                    count={totalTasks}
                    color="primary"
                    icon={<StarIcon />}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <CreateTaskModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          fetchStats();
        }}
      />
    </Box>
  );
};

export default WorkshopDashboard;